/**
 * Spinner Component (UX-14: Loading States)
 *
 * Use Spinners for ACTION LOADING (button clicks):
 * - Form submissions
 * - Button click actions
 * - API calls triggered by user action
 * - Short-duration loading (< 3 seconds expected)
 * - Inline loading states
 *
 * DO NOT use for:
 * - Initial page load (use Skeleton instead)
 * - Large content areas loading (use Skeleton instead)
 * - Table/list data loading (use Skeleton instead)
 *
 * Component Variants:
 * - Spinner: Basic spinner with size/color variants
 * - ButtonSpinner: Spinner with optional text, for buttons
 * - PageSpinner: Centered spinner for full page loading
 * - OverlaySpinner: Spinner with backdrop overlay
 *
 * Guidelines:
 * 1. Use ButtonSpinner inside buttons during form submission
 * 2. Use PageSpinner only for route transitions or critical data
 * 3. Use OverlaySpinner to prevent interaction during processing
 * 4. Always provide accessible labels via label prop
 * 5. Size guide: xs/sm for buttons, md for inline, lg for pages
 *
 * Examples:
 * - Submit button: <Button disabled={loading}>{loading ? <ButtonSpinner>Saving...</ButtonSpinner> : 'Save'}</Button>
 * - Inline action: <Spinner size="sm" />
 * - Page transition: <PageSpinner text="Loading..." />
 */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const spinnerVariants = cva("animate-spin", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      md: "h-6 w-6",
      lg: "h-8 w-8",
      xl: "h-12 w-12",
    },
    variant: {
      primary: "text-[var(--color-primary,#3b82f6)]",
      secondary: "text-[var(--color-secondary,#64748b)]",
      default: "text-gray-900 dark:text-gray-50",
      muted: "text-gray-500 dark:text-gray-400",
      white: "text-white",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "default",
  },
});

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {
  label?: string;
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size, variant, label, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="status"
        aria-label={label || "Loading"}
        aria-live="polite"
        aria-busy="true"
        className={cn("inline-flex items-center justify-center", className)}
        {...props}
      >
        <Loader2 className={cn(spinnerVariants({ size, variant }))} />
        <span className="sr-only">{label || "Loading..."}</span>
      </div>
    );
  }
);
Spinner.displayName = "Spinner";

// Centered page spinner for full page loading states
export interface PageSpinnerProps extends SpinnerProps {
  text?: string;
}

const PageSpinner = React.forwardRef<HTMLDivElement, PageSpinnerProps>(
  ({ text, size = "lg", className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex min-h-[400px] w-full flex-col items-center justify-center gap-4",
          className
        )}
        {...props}
      >
        <Spinner size={size} {...props} />
        {text && (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {text}
          </p>
        )}
      </div>
    );
  }
);
PageSpinner.displayName = "PageSpinner";

// Inline button spinner
export interface ButtonSpinnerProps extends SpinnerProps {
  children?: React.ReactNode;
}

const ButtonSpinner = React.forwardRef<HTMLDivElement, ButtonSpinnerProps>(
  ({ children, size = "sm", className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("inline-flex items-center gap-2", className)}
        {...props}
      >
        <Spinner size={size} {...props} />
        {children && <span>{children}</span>}
      </div>
    );
  }
);
ButtonSpinner.displayName = "ButtonSpinner";

// Overlay spinner for preserving layout during filtering/searching
export interface OverlaySpinnerProps extends SpinnerProps {
  show: boolean;
}

const OverlaySpinner = React.forwardRef<HTMLDivElement, OverlaySpinnerProps>(
  ({ show, size = "md", className, ...props }, ref) => {
    if (!show) return null;

    return (
      <div
        ref={ref}
        className={cn(
          "absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-sm",
          className
        )}
        {...props}
      >
        <Spinner size={size} {...props} />
      </div>
    );
  }
);
OverlaySpinner.displayName = "OverlaySpinner";

export { Spinner, PageSpinner, ButtonSpinner, OverlaySpinner, spinnerVariants };
